<template>
  <div class="auth-page theme-page">
    <div class="content-section auth-layout">
      <div class="container">
        <div class="auth-center">
          <div class="row justify-content-center">
            <div class="col-lg-5 col-md-7 col-sm-9 col-12 auth-card theme-panel">
              <div class="auth-brand">
                <div class="brand-mark" aria-hidden="true">
                  <span class="brand-bracket">&#91;</span>
                  <span class="brand-glyph"></span>
                  <span class="brand-bracket">&#93;</span>
                </div>
                <span class="brand-name">Abol Verify</span>
              </div>
              <h5 class="text-center title auth-title">Welcome Back</h5>
              <h6 class="sub-title theme-subtitle auth-copy text-center">
                Sign in with your email or phone number to continue.
              </h6>
              <p v-if="errorMessage" class="text-center text-danger mt-2">
                {{ errorMessage }}
              </p>

              <!-- Email / Phone selector -->
              <div class="auth-toggle" role="tablist" aria-label="Login method">
                <button
                  type="button"
                  role="tab"
                  :aria-selected="loginMode === 'email'"
                  :class="{ 'is-active': loginMode === 'email' }"
                  v-on:click="setMode('email')"
                >
                  Email
                </button>
                <button
                  type="button"
                  role="tab"
                  :aria-selected="loginMode === 'phone'"
                  :class="{ 'is-active': loginMode === 'phone' }"
                  v-on:click="setMode('phone')"
                >
                  Phone
                </button>
              </div>

              <Form class="form" @submit="login">
                <div v-if="loginMode === 'email'" class="form-group">
                  <Field
                    id="email"
                    v-model="loginObj.email"
                    :rules="{ required: ['Email'], email: ['Email'] }"
                    type="email"
                    name="email"
                    class="form-control shadow-none"
                    placeholder="Email"
                  />
                  <ErrorMessage name="email" class="form-text text-danger" />
                </div>

                <div v-else class="form-group">
                  <div class="phone-input-row">
                    <select
                      v-model="selectedCountryCode"
                      class="form-control shadow-none country-code-select"
                      aria-label="Country code"
                    >
                      <option
                        v-for="country in countryCodes"
                        :key="country.iso"
                        :value="country.code"
                      >
                        {{ country.label }}
                      </option>
                    </select>
                    <input
                      v-model.trim="phoneLocalNumber"
                      type="tel"
                      class="form-control shadow-none"
                      placeholder="Phone number"
                      autocomplete="tel-national"
                      v-on:input="onPhoneInput"
                    />
                  </div>
                  <span v-if="phoneError" class="form-text text-danger">{{ phoneError }}</span>
                </div>

                <div class="form-group">
                  <div class="password-field">
                    <Field
                      id="password"
                      v-model="loginObj.password"
                      :rules="{ required: ['Password'] }"
                      :type="showPassword ? 'text' : 'password'"
                      name="password"
                      class="form-control shadow-none"
                      placeholder="Password"
                    />
                    <button
                      type="button"
                      class="password-toggle"
                      v-on:click="showPassword = !showPassword"
                      :aria-label="showPassword ? 'Hide password' : 'Show password'"
                    >
                      <i :class="['pi', showPassword ? 'pi-eye-slash' : 'pi-eye']"></i>
                    </button>
                  </div>
                  <ErrorMessage name="password" class="form-text text-danger" />
                </div>
                <div class="d-flex justify-content-end">
                  <router-link :to="{ name: FORGOT_PASSWORD_ROUTE }" class="forgot-password">
                    Forgot password?
                  </router-link>
                </div>
                <button
                  type="submit"
                  class="btn btn-dark"
                  :disabled="isLoading ? true : false"
                >
                  <span
                    v-if="isLoading"
                    class="spinner-border spinner-border-sm"
                    role="status"
                    aria-hidden="true"
                  ></span>
                  <span v-if="!isLoading">Login</span>
                </button>

                <template v-if="googleEnabled">
                  <div class="divider"><span>or</span></div>
                  <div class="google-btn-wrap" ref="googleWrap">
                    <GoogleSignInButton
                      type="standard"
                      theme="outline"
                      size="large"
                      text="continue_with"
                      shape="pill"
                      logo-alignment="left"
                      :width="googleButtonWidth"
                      v-on:success="handleGoogleSuccess"
                      v-on:error="handleGoogleError"
                    />
                  </div>
                </template>
              </Form>
            </div>
          </div>
        </div>
      </div>
      <span class="text-center copyright auth-footer">
        &copy; Copyright {{ new Date().getFullYear() }} Abol Verify
      </span>
    </div>
  </div>
</template>
<script>
import { Field, Form, ErrorMessage, defineRule } from "vee-validate";
import { email } from "@vee-validate/rules";
import { GoogleSignInButton } from "vue3-google-signin";
import { DASHBOARD_ROUTE, FORGOT_PASSWORD_ROUTE, SIGNUP_ROUTE } from "@/router/routes";
import { GOOGLE_AUTH, LOGIN, LOGOUT } from "@/store/action_types";
import { TOAST_GLOBAL_CONFIG } from "@/utils/const";
import { isAdminRole } from "@/utils/enums";
import { COUNTRY_CODES, normalizePhoneNumber } from "@/utils/phone";

defineRule("email", email);
defineRule("required", (value, [fieldName = "Field"]) => {
  if (!value || value.length === 0) {
    return `${fieldName} is required`;
  }
  return true;
});

export default {
  title: "Login",
  components: {
    Field,
    Form,
    ErrorMessage,
    GoogleSignInButton,
  },
  data() {
    return {
      SIGNUP_ROUTE,
      FORGOT_PASSWORD_ROUTE,
      countryCodes: COUNTRY_CODES,
      loginMode: "email",
      selectedCountryCode: "+251",
      phoneLocalNumber: "",
      phoneError: "",
      showPassword: false,
      googleButtonWidth: 320,
      loginObj: {
        email: null,
        password: null,
      },
    };
  },
  computed: {
    errorMessage() {
      return this.$store.getters.errorMsg;
    },
    isLoading() {
      return this.$store.getters.account.isLoading;
    },
    // The Google buttons only render when a client ID is configured.
    googleEnabled() {
      return !!process.env.VUE_APP_GOOGLE_OAUTH_CLIENT_ID;
    },
  },
  methods: {
    setMode(mode) {
      this.loginMode = mode;
      this.phoneError = "";
    },
    onPhoneInput() {
      if (this.phoneError) this.phoneError = "";
    },
    login() {
      let payload;
      if (this.loginMode === "phone") {
        const phoneNumber = normalizePhoneNumber(this.selectedCountryCode, this.phoneLocalNumber);
        if (phoneNumber.replace(/\D/g, "").length < 8) {
          this.phoneError = "Enter a valid phone number.";
          return;
        }
        payload = { phoneNumber, password: this.loginObj.password };
      } else {
        payload = { email: this.loginObj.email, password: this.loginObj.password };
      }

      this.$store.dispatch(LOGIN, payload).then((res) => {
        if (res) {
          this.enforceAdminThenRoute();
        }
      });
    },
    // abol-verify-admin only admits admin / super-admin accounts. Any other
    // authenticated account is signed back out with a clear message.
    enforceAdminThenRoute() {
      const account = this.$store.getters.account.data;
      if (account && isAdminRole(account.role)) {
        this.$router.push({ name: DASHBOARD_ROUTE });
        return;
      }
      this.$store.dispatch(LOGOUT);
      this.$toast.error(
        "Access denied. This platform is for administrators only.",
        TOAST_GLOBAL_CONFIG,
      );
    },
    handleGoogleSuccess(response) {
      const idToken = response.credential;
      if (!idToken) {
        this.$toast.error("Google sign-in failed. Please try again.", TOAST_GLOBAL_CONFIG);
        return;
      }
      this.$store.dispatch(GOOGLE_AUTH, { idToken }).then((res) => {
        if (res) {
          this.enforceAdminThenRoute();
        } else {
          this.$toast.error(
            this.$store.getters.errorMsg || "Google sign-in failed. Please try again.",
            TOAST_GLOBAL_CONFIG,
          );
        }
      });
    },
    handleGoogleError() {
      this.$toast.error("Google sign-in failed. Please try again.", TOAST_GLOBAL_CONFIG);
    },
    // Google's button needs an explicit pixel width, so match it to the form
    // width (same as the full-width Login button) and keep it in sync on resize.
    updateGoogleButtonWidth() {
      const el = this.$refs.googleWrap;
      if (el && el.clientWidth) {
        this.googleButtonWidth = Math.min(400, Math.max(200, Math.round(el.clientWidth)));
      }
    },
  },
  mounted() {
    this.$nextTick(this.updateGoogleButtonWidth);
    window.addEventListener("resize", this.updateGoogleButtonWidth);
  },
  beforeUnmount() {
    window.removeEventListener("resize", this.updateGoogleButtonWidth);
  },
};
</script>
<style scoped>
.auth-brand {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 10px;
  margin-bottom: 24px;
}

.brand-mark {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 2px;
  width: 32px;
  height: 32px;
  border-radius: var(--ehs-radius-sm);
  background: linear-gradient(135deg, var(--ehs-color-primary) 0%, #bcdc44 100%);
  color: #ffffff;
  font-weight: 700;
  font-size: 0.75rem;
  line-height: 1;
  letter-spacing: -0.05em;
  box-shadow: 0 4px 12px rgba(115, 196, 29, 0.3),
    inset 0 1px 0 rgba(255, 255, 255, 0.2);
}

.brand-bracket {
  font-family: var(--ehs-font-mono);
  font-size: 1rem;
  font-weight: 600;
  opacity: 0.9;
  line-height: 1;
}

.brand-glyph {
  display: inline-block;
  width: 5px;
  height: 5px;
  background-color: #ffffff;
  border-radius: 1px;
  margin: 0 1px;
}

.brand-name {
  color: var(--ehs-color-ink);
  font-family: var(--ehs-font-display);
  font-size: 1.1rem;
  font-weight: 700;
  letter-spacing: -0.035em;
}

.auth-toggle {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 4px;
  margin-top: 1.5rem;
  padding: 4px;
  background: var(--ehs-color-canvas-strong);
  border: 1px solid var(--ehs-color-border);
  border-radius: var(--ehs-radius-md);
}

.auth-toggle button {
  border: 0;
  background: transparent;
  padding: 8px 14px;
  border-radius: var(--ehs-radius-sm);
  font-size: 0.82rem;
  font-weight: 600;
  color: var(--ehs-color-muted);
  cursor: pointer;
  transition: background 0.15s ease, color 0.15s ease;
}

.auth-toggle button.is-active {
  background: var(--ehs-color-surface);
  color: var(--ehs-color-primary);
  box-shadow: var(--ehs-shadow-xs);
}

.form {
  margin-top: 1.25rem;
}

.sub-title {
  margin-top: 0.5rem;
}

.phone-input-row {
  display: grid;
  grid-template-columns: minmax(118px, 0.4fr) minmax(0, 1fr);
  gap: 12px;
}

.country-code-select {
  padding-left: 10px !important;
  padding-right: 10px !important;
}

.password-field {
  position: relative;
}

.password-field :deep(input) {
  padding-right: 44px !important;
}

.password-toggle {
  position: absolute;
  top: 0;
  right: 0;
  height: 42px;
  width: 42px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  border: 0;
  background: transparent;
  color: var(--ehs-color-soft);
  cursor: pointer;
  box-shadow: none !important;
  transition: color 0.15s ease;
}

.password-toggle:hover {
  color: var(--ehs-color-muted);
}

.password-toggle .pi {
  font-size: 0.9rem;
}

.google-btn-wrap {
  display: flex;
  justify-content: center;
  margin-bottom: 6px;
}

.auth-switch-link {
  margin-top: 16px;
  margin-bottom: 0;
}

@media (max-width: 991.98px) {
  .auth-card {
    padding: 22px 18px !important;
  }

  .auth-center {
    transform: none;
    padding: 24px 0 80px;
  }

  .auth-title {
    font-size: 1.65rem !important;
  }
}
</style>
